import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { fetchQuery, requestSubscription, useRelayEnvironment } from 'react-relay';

import Avatar from '@atlaskit/avatar/avatar';
import { usePublish } from '@atlaskit/rovo-triggers/main';

import { ConfluencePageAri } from '@atlassian/ari';
import { type ConversationAction, HandleActionType } from '@atlassian/rovo-conversation-actions';

import { fg } from '@confluence/feature-gating';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import type {
	EditPageAgentSessionNudgesQuery as EditPageAgentSessionNudgesQueryType,
	EditPageAgentSessionNudgesQuery$data,
} from '../__generated__/EditPageAgentSessionNudgesQuery.graphql.ts';
import EditPageAgentSessionNudgesQuery from '../__generated__/EditPageAgentSessionNudgesQuery.graphql.ts';
import type {
	EditPageAgentSessionNudgesSubscription as EditPageAgentSessionNudgesSubscriptionType,
	EditPageAgentSessionNudgesSubscription$data,
} from '../__generated__/EditPageAgentSessionNudgesSubscription.graphql.ts';
import EditPageAgentSessionNudgesSubscription from '../__generated__/EditPageAgentSessionNudgesSubscription.graphql.ts';
import type {
	EditPageRovoActivationQuery as EditPageRovoActivationQueryType,
	EditPageRovoActivationQuery$data,
} from '../__generated__/EditPageRovoActivationQuery.graphql.ts';
import EditPageRovoActivationQuery from '../__generated__/EditPageRovoActivationQuery.graphql.ts';

const PAGE_SIZE = 25;

const messages = defineMessages({
	fallbackTitle: {
		id: 'edit-page-rovo-config-provider.agent-session-nudge.fallback-title.ai-non-final',
		defaultMessage: 'Rovo conversation',
		description:
			'Fallback title for an unread Rovo conversation shown beside the Rovo button on an edit page.',
	},
	needsInputSecondaryTitle: {
		id: 'edit-page-rovo-config-provider.agent-session-nudge.needs-input-secondary-title.ai-non-final',
		defaultMessage: 'Needs your input',
		description:
			'Secondary text for an edit-page Rovo conversation that is waiting for the user to respond.',
	},
});

type AssociationQuery = NonNullable<
	EditPageAgentSessionNudgesQuery$data['agent_sessionAssociationQuery']
>;
export type AgentSessionAssociation = NonNullable<NonNullable<AssociationQuery['nodes']>[number]>;

export type EligibleAgentSession = Readonly<{
	agentPicture: string | null;
	channelId: string;
	channelName: string | null;
	sessionId: string;
}>;

// These values are interpolated inside quoted AQL literals. Escape the literal delimiters so an
// account ID can never produce malformed AQL or change the intended predicate structure.
export const escapeAqlValue = (value: string): string =>
	value.replace(/\\/g, '\\\\').replace(/'/g, "\\'");

const INVOCATION_TYPE_FILTER = [
	'CONFLUENCE_COMMENT_MENTION',
	'CONFLUENCE_CHAT_EDIT',
	'CONFLUENCE_INLINE_EDIT',
	'CONFLUENCE_CWR_EDIT',
	'CONFLUENCE_CWR_CREATE',
	'CONFLUENCE_CWR_EXISTING',
	'CONFLUENCE_REMIX',
	'CONFLUENCE_REMIX_EDIT',
	'CONFLUENCE_CHAT',
]
	.map((invocationType) => `invocationType = '${invocationType}'`)
	.join('\n  OR ');

export const buildAgentSessionAql = (userId: string, pageAri: string): string =>
	`userAccountId = '${escapeAqlValue(userId)}'\n` +
	`AND entityId = '${escapeAqlValue(pageAri)}'\n` +
	"AND deleted = 'false'\n" +
	"AND (state = 'INPUT_REQUIRED' OR state = 'AUTH_REQUIRED')\n" +
	`AND (\n  ${INVOCATION_TYPE_FILTER}\n)`;

export const selectEligibleAgentSessions = (
	nodes: readonly (AgentSessionAssociation | null)[] | null | undefined,
): EligibleAgentSession[] => {
	// Multiple association records can resolve to the same conversation channel. Deduplicate them so
	// the same conversation is not registered as more than one nudge.
	const channelIds = new Set<string>();
	const eligible: EligibleAgentSession[] = [];

	for (const association of nodes ?? []) {
		const session = association?.agentSession;
		const channel = session?.conversationChannel;
		// readStatus is resolved from the workspace-specific conversation channel and is not an
		// AgentSessionAssociation AQL field, so unread filtering must happen after hydration.
		if (
			!session?.id ||
			!channel?.id ||
			channel.readStatus !== false ||
			channelIds.has(channel.id)
		) {
			continue;
		}

		channelIds.add(channel.id);
		eligible.push({
			agentPicture: session.agent?.picture ?? null,
			channelId: channel.id,
			channelName: channel.name ?? null,
			sessionId: session.id,
		});
	}

	// Preserve the server's lastStateChangeAt DESC order.
	return eligible;
};

export type EditPageAgentSessionNudgesResult = Readonly<{
	errors: readonly Error[];
	nudges: readonly ConversationAction[];
}>;

export function useEditPageAgentSessionNudges(contentId: string): EditPageAgentSessionNudgesResult {
	const { cloudId, isLoggedIn, userId } = useSessionData();
	const { formatMessage } = useIntl();
	const publish = usePublish('ai-mate');
	const environment = useRelayEnvironment();
	const [activationData, setActivationData] = useState<EditPageRovoActivationQuery$data>();
	const [activationError, setActivationError] = useState<Error>();
	const [sessionData, setSessionData] = useState<EditPageAgentSessionNudgesQuery$data>();
	const [sessionError, setSessionError] = useState<Error>();
	const [subscriptionError, setSubscriptionError] = useState<Error>();
	// A session-change event can start a new fetch outside the initial query effect. Keep the latest
	// query subscription in a ref so a refresh can cancel an in-flight fetch and effect cleanup can
	// unsubscribe whichever fetch is current without causing another render.
	const sessionQuerySubscriptionRef = useRef<{ unsubscribe: () => void } | null>(null);

	const pageAri = useMemo(
		() =>
			cloudId && contentId
				? ConfluencePageAri.create({ siteId: cloudId, pageId: contentId }).toString()
				: '',
		[cloudId, contentId],
	);

	const canLoadActivation = Boolean(
		isLoggedIn && userId && cloudId && contentId && fg('confluence_rovo_agent_session_nudges'),
	);

	useEffect(() => {
		if (!canLoadActivation) {
			setActivationData(undefined);
			setActivationError(undefined);
			return;
		}

		const activationQuerySubscription = fetchQuery<EditPageRovoActivationQueryType>(
			environment,
			EditPageRovoActivationQuery,
			{ cloudId },
			{ fetchPolicy: 'store-or-network' },
		).subscribe({
			next: (data) => {
				setActivationData(data);
				setActivationError(undefined);
			},
			error: (error: Error) => setActivationError(error),
		});

		return () => activationQuerySubscription.unsubscribe();
	}, [canLoadActivation, cloudId, environment]);

	const rovoActivationId = activationData?.tenantContexts?.[0]?.activationIdByProduct?.active;
	const workspaceAri = rovoActivationId ? `ari:cloud:rovo::workspace/${rovoActivationId}` : '';
	const aqlQuery = useMemo(
		() => (userId && pageAri ? buildAgentSessionAql(userId, pageAri) : ''),
		[userId, pageAri],
	);
	const hasAgentSessionNudgeContext = Boolean(
		isLoggedIn && aqlQuery && workspaceAri && fg('confluence_rovo_agent_session_nudges'),
	);

	const fetchSessions = useCallback(
		(isRefresh = false) => {
			if (!hasAgentSessionNudgeContext) {
				return;
			}

			sessionQuerySubscriptionRef.current?.unsubscribe();
			sessionQuerySubscriptionRef.current = fetchQuery<EditPageAgentSessionNudgesQueryType>(
				environment,
				EditPageAgentSessionNudgesQuery,
				{
					aqlQuery,
					shardingContext: workspaceAri,
					first: PAGE_SIZE,
				},
				{ fetchPolicy: isRefresh ? 'network-only' : 'store-or-network' },
			).subscribe({
				next: (data) => {
					setSessionData(data);
					setSessionError(undefined);
				},
				error: (error: Error) => setSessionError(error),
			});
		},
		[aqlQuery, environment, hasAgentSessionNudgeContext, workspaceAri],
	);

	useEffect(() => {
		if (!hasAgentSessionNudgeContext) {
			setSessionData(undefined);
			setSessionError(undefined);
			return;
		}

		fetchSessions();
		return () => sessionQuerySubscriptionRef.current?.unsubscribe();
	}, [fetchSessions, hasAgentSessionNudgeContext]);

	const connection = sessionData?.agent_sessionAssociationQuery;
	const eligibleSessions = useMemo(() => {
		if (!hasAgentSessionNudgeContext) {
			return [];
		}

		return selectEligibleAgentSessions(connection?.nodes);
	}, [hasAgentSessionNudgeContext, connection?.nodes]);

	useEffect(() => {
		if (!hasAgentSessionNudgeContext || !userId || !cloudId) {
			setSubscriptionError(undefined);
			return;
		}

		const inboxSessionChangedSubscription =
			requestSubscription<EditPageAgentSessionNudgesSubscriptionType>(environment, {
				subscription: EditPageAgentSessionNudgesSubscription,
				variables: {
					accountId: userId,
					cloudId,
					workspaceAri,
				},
				onNext: (_data: EditPageAgentSessionNudgesSubscription$data | null | undefined) => {
					setSubscriptionError(undefined);
					fetchSessions(true);
				},
				onError: (error: Error) => setSubscriptionError(error),
			});

		return () => inboxSessionChangedSubscription.dispose();
	}, [cloudId, environment, fetchSessions, hasAgentSessionNudgeContext, userId, workspaceAri]);

	const nudges = useMemo<ConversationAction[]>(
		() =>
			eligibleSessions.map((session) => {
				const actionId = `agent-session-nudge-${session.sessionId}`;
				return {
					id: actionId,
					title: session.channelName || formatMessage(messages.fallbackTitle),
					secondaryTitle: formatMessage(messages.needsInputSecondaryTitle),
					proactiveNudgeStatus: 'needs-input',
					icon: () =>
						session.agentPicture ? (
							<Avatar appearance="hexagon" name="" size="xxsmall" src={session.agentPicture} />
						) : (
							<></>
						),
					onClick: () =>
						publish({
							type: 'chat-open',
							data: { channelId: session.channelId },
							openChatMode: 'mini-modal',
							source: 'confluence-agent-session-nudge',
						}),
					type: 'ai-action',
					handleType: HandleActionType.Base,
					nudgeContext: {
						id: actionId,
						selfEligible: true,
						suppressionKey: session.channelId,
					},
				};
			}),
		[eligibleSessions, formatMessage, publish],
	);
	const errors = useMemo(
		() =>
			[activationError, sessionError, subscriptionError].filter(
				(error): error is Error => error instanceof Error,
			),
		[activationError, sessionError, subscriptionError],
	);

	return {
		nudges,
		errors,
	};
}
